// ── Vista previa del formulario, al lado de los campos ───────────────────────
// Antes vivía en una pestaña propia: para ver el efecto de un campo había que
// cambiar de pestaña, mirar, y volver. Acá va en la misma pantalla, a la
// derecha de la lista de campos.
//
// Se ve SIEMPRE. Estuvo colapsado un tiempo porque la columna medía 380px y
// abrirlo le comía el ancho a la lista de campos; desde que la pestaña es mitad
// y mitad esa razón desapareció, y una vista previa que hay que abrir cada vez
// no es una vista previa.
//
// Dos cosas que resuelve y por qué:
//
// 1. ESCALADO cuando la columna queda corta. El formulario público está
//    pensado para ~560px: meterlo en 380 lo rompe. Se escala con transform
//    (el iframe sigue midiendo 560 por dentro) y se reserva el alto real de
//    la caja escalada, que si no deja un hueco muerto abajo.
// 2. DOBLE CAPA para el refresco. Recargar el iframe en su lugar lo deja en
//    blanco mientras carga y la vista previa parpadea con cada tecla. Se monta
//    el nuevo detrás, y recién cuando cargó se cambia cuál se ve.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Ancho al que está pensado el formulario público. */
const ANCHO_BASE = 560;

export function FormPreviewDock({ publicUrl, previewKey }: {
  publicUrl: string;
  /** Cambia cuando cambió algo que altera el render (campos, marca, título). */
  previewKey: string;
}) {
  const { t } = useTranslation();
  const [alto, setAlto] = useState(760);
  const [ancho, setAncho] = useState(ANCHO_BASE);
  const contenedor = useRef<HTMLDivElement | null>(null);

  // Dos capas: la que se ve y la que está cargando. Cuando la de atrás
  // termina, pasa a ser la de adelante y la vieja se descarta.
  const [capaVisible, setCapaVisible] = useState(previewKey);
  const [capaCargando, setCapaCargando] = useState<string | null>(null);

  useEffect(() => {
    if (previewKey !== capaVisible) setCapaCargando(previewKey);
  }, [previewKey, capaVisible]);

  // Alto real del formulario — lo manda la página pública por postMessage.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const data = e.data as { source?: string; height?: number } | null | undefined;
      if (data?.source === 'evo-form' && typeof data.height === 'number') {
        setAlto(Math.max(320, data.height));
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Ancho disponible de la columna, para decidir si hay que escalar.
  useEffect(() => {
    const el = contenedor.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? ANCHO_BASE;
      setAncho(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const escala = Math.min(1, ancho / ANCHO_BASE);
  const altoCaja = Math.round(alto * escala);
  // Centrado horizontal. Con la columna más ancha que 560 el formulario ya no
  // se escala, así que sobra espacio y quedaba pegado a la izquierda. Cuando
  // sí se escala, la caja ocupa todo el ancho y esto da 0.
  const margenIzq = Math.max(0, Math.round((ancho - ANCHO_BASE * escala) / 2));

  const capa = (clave: string, visible: boolean) => (
    <iframe
      key={clave}
      src={publicUrl}
      title={t('forms.detail.previewTitle')}
      onLoad={() => {
        if (clave === capaCargando) {
          setCapaVisible(clave);
          setCapaCargando(null);
        }
      }}
      style={{
        position: 'absolute', top: 0, left: `${margenIzq}px`,
        width: `${ANCHO_BASE}px`,
        height: `${alto}px`,
        border: 'none',
        transform: `scale(${escala})`,
        transformOrigin: 'top left',
        // La capa de atrás no recibe clics ni se ve: solo carga.
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 120ms linear',
      }}
    />
  );

  return (
    <div style={{
      border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r-md)',
      overflow: 'hidden',
      background: 'var(--evo-bg-2)',
      alignSelf: 'flex-start',
    }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)',
          padding: 'var(--evo-card-head-pad)',
          borderBottom: '1px solid var(--evo-stroke)',
          userSelect: 'none',
        }}
      >
        <span style={{
          flex: 1, minWidth: 0,
          fontSize: 'var(--evo-fs-caption)', fontWeight: 'var(--evo-fw-medium)',
          fontFamily: 'var(--evo-font)', letterSpacing: 'var(--evo-ls-caps)',
          textTransform: 'uppercase', color: 'var(--evo-fg-3)',
        }}>
          {t('forms.detail.tabPreview')}
        </span>
        {escala < 1 && (
          <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
            {Math.round(escala * 100)}%
          </span>
        )}
      </div>

      <div ref={contenedor} style={{ position: 'relative', height: `${altoCaja}px`, background: '#f1f5f9', transition: 'height 150ms ease' }}>
        {capa(capaVisible, true)}
        {capaCargando && capa(capaCargando, false)}
      </div>
    </div>
  );
}
